import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { Area, AreaChart, Line, LineChart, XAxis, YAxis, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";
import { CycleTimeTrendPoint } from "@/hooks/useCycleTimeAnalytics";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingDown, TrendingUp } from "lucide-react";

interface CycleTimeTrendChartProps {
  data: CycleTimeTrendPoint[];
  isLoading: boolean;
}

export function CycleTimeTrendChart({ data, isLoading }: CycleTimeTrendChartProps) {
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Cycle Time Trend</CardTitle>
          <CardDescription>Track efficiency improvements over time</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Cycle Time Trend</CardTitle>
          <CardDescription>Track efficiency improvements over time</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No data available for the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Calculate trend direction
  const firstHalf = data.slice(0, Math.floor(data.length / 2));
  const secondHalf = data.slice(Math.floor(data.length / 2));
  
  const firstAvg = firstHalf.reduce((sum, d) => sum + d.avgDays, 0) / (firstHalf.length || 1);
  const secondAvg = secondHalf.reduce((sum, d) => sum + d.avgDays, 0) / (secondHalf.length || 1);
  
  const isImproving = secondAvg < firstAvg;
  const trendPercent = firstAvg > 0 ? Math.abs((secondAvg - firstAvg) / firstAvg * 100) : 0;

  const chartConfig = {
    avgDays: {
      label: 'Average Cycle Time',
      color: 'hsl(var(--primary))',
    },
    medianDays: {
      label: 'Median Cycle Time',
      color: 'hsl(var(--muted-foreground))',
    },
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Cycle Time Trend</CardTitle>
            <CardDescription>Track efficiency improvements over time</CardDescription>
          </div>
          {data.length >= 4 && trendPercent > 5 && (
            <div className={`flex items-center gap-1.5 text-sm font-medium ${
              isImproving ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
            }`}>
              {isImproving ? (
                <>
                  <TrendingDown className="h-4 w-4" />
                  <span>Improving {trendPercent.toFixed(0)}%</span>
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4" />
                  <span>Slower by {trendPercent.toFixed(0)}%</span>
                </>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="cycleTimeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
              <XAxis 
                dataKey="period" 
                axisLine={false}
                tickLine={false}
                fontSize={12}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                fontSize={12}
                tickFormatter={(value) => `${value}d`}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <ChartTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const data = payload[0].payload;
                  return (
                    <div className="bg-popover border rounded-lg shadow-lg p-3 space-y-1">
                      <p className="font-medium">{data.period}</p>
                      <p className="text-sm text-muted-foreground">{data.jobCount} jobs completed</p>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-full bg-primary" />
                        <span>Average: {data.avgDays.toFixed(1)} days</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-full border-2 border-muted-foreground bg-transparent" />
                        <span>Median: {data.medianDays.toFixed(1)} days</span>
                      </div>
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="avgDays"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#cycleTimeGradient)"
                dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                activeDot={{ r: 6, fill: 'hsl(var(--primary))' }}
              />
              <Line
                type="monotone"
                dataKey="medianDays"
                stroke="hsl(var(--muted-foreground))"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
        <div className="flex justify-center gap-6 mt-4">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-muted-foreground">Average</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full border-2 border-muted-foreground bg-transparent" />
            <span className="text-muted-foreground">Median</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
